import React, { useEffect, useReducer } from "react";
import { useWeb3React } from "@web3-react/core";
import { AbstractConnector } from "@web3-react/abstract-connector";
import { Button, Text, Flex, Link, Box, Heading } from "theme-ui";

import { Wallets } from "../config/web3/wallets";
import { useAuthorizedConnection } from "../hooks/useAuthorizedConnection";

import { RetryDialog } from "./RetryDialog";
import { ConnectionConfirmationDialog } from "./ConnectionConfirmationDialog";
import { Icon } from "./Icon";
import { Modal } from "./Modal";

type ConnectionState =
  | { type: "inactive" }
  | {
      type: "activating" | "active" | "rejectedByUser" | "alreadyPending" | "failed";
      connector: AbstractConnector;
    };

type ConnectionAction =
  | { type: "startActivating"; connector: AbstractConnector }
  | { type: "fail"; error: Error }
  | { type: "finishActivating" | "retry" | "cancel" | "deactivate" };

const connectionReducer: React.Reducer<ConnectionState, ConnectionAction> = (state, action) => {
  switch (action.type) {
    case "startActivating":
      return {
        type: "activating",
        connector: action.connector
      };
    case "finishActivating":
      return {
        type: "active",
        connector: state.type === "inactive" ? Wallets.METAMASK.connector : state.connector
      };
    case "fail":
      if (state.type !== "inactive") {
        return {
          type: action.error.message.match(/user rejected/i)
            ? "rejectedByUser"
            : action.error.message.match(/already pending/i)
            ? "alreadyPending"
            : "failed",
          connector: state.connector
        };
      }
      break;
    case "retry":
      if (state.type !== "inactive") {
        return {
          type: "activating",
          connector: state.connector
        };
      }
      break;
    case "cancel":
      return {
        type: "inactive"
      };
    case "deactivate":
      return {
        type: "inactive"
      };
  }

  console.warn("Ignoring connectionReducer action:");
  console.log(action);
  console.log("  in state:");
  console.log(state);

  return state;
};

type WalletConnectorProps = {
  loader?: React.ReactNode;
  children?: React.ReactNode;
};

export const WalletConnector: React.FC<WalletConnectorProps> = ({ children, loader }) => {
  const { activate, deactivate, active, error } = useWeb3React<unknown>();
  const triedAuthorizedConnection = useAuthorizedConnection();
  const [connectionState, dispatch] = useReducer(connectionReducer, { type: "inactive" });

  useEffect(() => {
    if (error) {
      dispatch({ type: "fail", error });
      deactivate();
    }
  }, [error, deactivate]);

  useEffect(() => {
    if (active) {
      dispatch({ type: "finishActivating" });
    } else {
      dispatch({ type: "deactivate" });
    }
  }, [active]);

  if (!triedAuthorizedConnection) {
    return <>{loader}</>;
  }

  const handleConnect = (wallet: typeof Wallets[keyof typeof Wallets]) => {
    dispatch({ type: "startActivating", connector: wallet.connector });
    activate(wallet.connector);
  };

  return (
    <>
      {/* Always render the app, but blur it when not connected */}
      <Box
        sx={{
          filter: !active ? "blur(8px)" : "none",
          pointerEvents: !active ? "none" : "auto",
          transition: "filter 0.3s ease"
        }}
      >
        {children}
      </Box>

      {/* Show wallet modal overlay when not connected */}
      {!active && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bg: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 3
          }}
        >
          <Box
            sx={{
              bg: "white",
              borderRadius: "16px",
              padding: 5,
              maxWidth: "480px",
              width: "100%",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            }}
          >
            <Flex sx={{ flexDirection: "column", alignItems: "center", gap: 4 }}>
              <Icon name="wallet" size="3x" />
              <Heading as="h2" sx={{ fontSize: 4, fontWeight: "700", textAlign: "center" }}>
                Connect Your Wallet
              </Heading>
              <Text sx={{ textAlign: "center", color: "#6B7280", fontSize: 2 }}>
                Connect your wallet to view your positions, borrow USDS, earn rewards, and access
                all features of Sable Finance.
              </Text>

              <Flex sx={{ flexDirection: "column", width: "100%", gap: 2, mt: 2 }}>
                {Object.values(Wallets).map(wallet => (
                  <Button
                    key={wallet.id}
                    variant="outline"
                    onClick={() => handleConnect(wallet)}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      gap: 3,
                      width: "100%",
                      py: 3,
                      px: 4,
                      fontSize: 2,
                      fontWeight: "600"
                    }}
                  >
                    <img src={wallet.icon} alt={wallet.name} height="32px" width="32px" />
                    <Text>{wallet.name}</Text>
                  </Button>
                ))}
              </Flex>

              <Text sx={{ fontSize: 1, color: "#9CA3AF", textAlign: "center", mt: 2 }}>
                Don't have a wallet?{" "}
                <Link
                  href="https://metamask.io/download.html"
                  target="_blank"
                  sx={{ color: "accent", fontWeight: "600" }}
                >
                  Get MetaMask <Icon size="xs" name="external-link-alt" />
                </Link>
              </Text>
            </Flex>
          </Box>
        </Box>
      )}

      {connectionState.type === "failed" && (
        <Modal>
          <RetryDialog
            title={"Failed to connect wallet"}
            onCancel={() => dispatch({ type: "cancel" })}
            onRetry={() => {
              dispatch({ type: "retry" });
              activate(connectionState.connector);
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              You might need to install MetaMask or use a different browser.
            </Box>
            <Link sx={{ lineHeight: 3 }} href="https://metamask.io/download.html" target="_blank">
              Learn more <Icon size="xs" name="external-link-alt" />
            </Link>
          </RetryDialog>
        </Modal>
      )}

      {connectionState.type === "activating" && (
        <Modal>
          <ConnectionConfirmationDialog
            title={"Confirm connection in your wallet"}
            icon={<Icon name="wallet" size="lg" />}
            onCancel={() => dispatch({ type: "cancel" })}
          >
            <Text sx={{ textAlign: "center" }}>
              Confirm the request that&apos;s just appeared.
              <> If you can&apos;t see a request, you might have to open your wallet.</>
            </Text>
          </ConnectionConfirmationDialog>
        </Modal>
      )}

      {connectionState.type === "rejectedByUser" && (
        <Modal>
          <RetryDialog
            title="Cancel connection?"
            onCancel={() => dispatch({ type: "cancel" })}
            onRetry={() => {
              dispatch({ type: "retry" });
              activate(connectionState.connector);
            }}
          >
            <Text>To use Sable, you need to connect your wallet account.</Text>
          </RetryDialog>
        </Modal>
      )}

      {connectionState.type === "alreadyPending" && (
        <Modal>
          <RetryDialog
            title="Connection already requested"
            onCancel={() => dispatch({ type: "cancel" })}
            onRetry={() => {
              dispatch({ type: "retry" });
              activate(connectionState.connector);
            }}
          >
            <Text>Please check your wallet and accept the connection request before retrying.</Text>
          </RetryDialog>
        </Modal>
      )}
    </>
  );
};
